import React, { useCallback, useMemo } from 'react';

import { AGGREGATION_FIELDS } from '@/shared/api/openmetadata';
import type { OpenMetadataAggregations } from '@/shared/api/openmetadata';
import { getIconForEntityType, normalizeEntityType } from '@/shared/lib/asset';
import { highlightText } from '@/shared/lib/text';
import { FilterDropdown } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';

import { DISCOVERY_SEARCH_EXCLUDED_ASSET_TYPES } from '@/entities/asset-search/domain/constants';
import { useFilterStore } from '@/entities/discovery';
import type { SearchResultType } from '@/entities/search';
import { useSearchStore } from '@/entities/search/domain/store';

import styles from './AssetTypeDropdown.module.css';

const ENTITY_TYPE_AGGREGATION_BUCKET_EXCLUDE = new Set<string>([
  'storedProcedure',
  ...DISCOVERY_SEARCH_EXCLUDED_ASSET_TYPES,
]);
const ENTITY_TYPE_LABELS: Partial<Record<SearchResultType, string>> = {
  database: 'Database',
  databaseSchema: 'Schema',
  table: 'Table',
  container: 'Bucket',
  pipeline: 'Pipeline',
  topic: 'Topic',
  experiment: 'Experiment',
  run: 'Run',
  dashboard: 'Dashboard',
  dataset: 'Dataset',
  file: 'File',
  glossaryTerm: 'Glossary Term',
  tag: 'Tag',
  dataProduct: 'Data Product',
  feature_set: 'Feature Set',
  artifact: 'Artifact',
} as const;

const ASSET_TYPE_ORDER: SearchResultType[] = [
  'database',
  'databaseSchema',
  'table',
  'container',
  'tag',
  'glossaryTerm',
  'experiment',
  'run',
  'feature_set',
  'dataset',
  'artifact',
];

const getLabelForType = (type: SearchResultType): string => {
  return ENTITY_TYPE_LABELS[type] || type;
};

const generateAssetTypeOptionsFromAggregations = (
  initialAggregations: OpenMetadataAggregations | null,
  selectedTypes: string[]
): FilterDropdownOption<SearchResultType>[] => {
  if (!initialAggregations) return [];

  const initialBuckets = (initialAggregations[AGGREGATION_FIELDS.ENTITY_TYPE]?.buckets || []).filter(
    (bucket) => !ENTITY_TYPE_AGGREGATION_BUCKET_EXCLUDE.has(bucket.key)
  );

  const bucketMap = new Map(initialBuckets.map((bucket) => [bucket.key, bucket.doc_count]));

  const options: FilterDropdownOption<SearchResultType>[] = ASSET_TYPE_ORDER.map((type) => {
    const count = bucketMap.get(type) || 0;
    return {
      key: type,
      label: getLabelForType(type),
      value: type,
      disabled: false,
      data: {
        icon: getIconForEntityType(type),
        count,
        sortOrder: ASSET_TYPE_ORDER.indexOf(type),
      },
    };
  });

  initialBuckets.forEach((bucket) => {
    const type = bucket.key as SearchResultType;
    if (!ASSET_TYPE_ORDER.includes(type)) {
      options.push({
        key: type,
        label: getLabelForType(type),
        value: type,
        disabled: false,
        data: {
          icon: getIconForEntityType(type),
          count: bucket.doc_count,
          sortOrder: -1,
        },
      });
    }
  });

  const getOrder = (opt: FilterDropdownOption<SearchResultType>) => {
    const order = opt.data?.sortOrder;
    return typeof order === 'number' && order !== -1 ? order : Infinity;
  };

  return options.sort((a, b) => {
    const aSelected = selectedTypes.includes(a.key);
    const bSelected = selectedTypes.includes(b.key);

    if (aSelected !== bSelected) {
      return aSelected ? -1 : 1;
    }

    if (a.disabled !== b.disabled) {
      return a.disabled ? 1 : -1;
    }

    const orderA = getOrder(a);
    const orderB = getOrder(b);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return a.label.localeCompare(b.label);
  });
};

type AssetTypeDropdownProps = {
  width?: number | string;
};

export const AssetTypeDropdown: React.FC<AssetTypeDropdownProps> = ({ width }) => {
  const { filters } = useFilterStore();
  const { initialAggregations } = useSearchStore();

  const selectedTypes = useMemo(() => {
    const rawTypes = filters.assetType || [];
    const normalizedTypes = rawTypes.map(normalizeEntityType);
    const uniqueTypes = Array.from(new Set(normalizedTypes));
    return uniqueTypes.filter((type) => ENTITY_TYPE_LABELS[type as SearchResultType]);
  }, [filters.assetType]);

  const assetTypeOptions = useMemo(() => {
    return generateAssetTypeOptionsFromAggregations(initialAggregations, selectedTypes);
  }, [initialAggregations, selectedTypes]);

  const handleSelectionChange = useCallback((values: string[]) => {
    const store = useFilterStore.getState();
    store.updateAssetTypeFilter(values, 'topBar');
    store.updateQuickFilter(undefined);
  }, []);

  return (
    <FilterDropdown
      buttonText="데이터 유형"
      searchPlaceholder="데이터 유형 검색..."
      selectedValues={selectedTypes}
      options={assetTypeOptions}
      onSelectionChange={handleSelectionChange}
      width={width}
      renderItem={(option, _isSelected, _onToggle, searchQuery) => (
        <div className={styles.listContent}>
          <div className={styles.listInfo}>
            <div className={styles.listLabelWithIcon}>
              {highlightText(option.label, searchQuery, { className: 'highlight' })}
            </div>
          </div>
        </div>
      )}
    />
  );
};
